/**
 * Motor Circuit Protector (MCP) Sizing Calculation
 *
 * Per NEC 2023 Article 430.52, motor branch-circuit short-circuit and
 * ground-fault protection must not exceed the values in Table 430.52.
 *
 * This module calculates the appropriate MCP or breaker size based on:
 * - Motor FLA (from NEC tables)
 * - Motor design letter (affects maximum allowed percentage)
 * - Protection device type
 * - Whether a VFD is present (changes sizing approach)
 */

import type { NECReference } from '../types';
import {
  type MotorDesign,
  type ProtectionDeviceType,
  getMCPMaxPercent,
  roundToStandardSize,
  NEC_TABLE_430_52_REF,
  NEC_SECTION_240_6_REF,
  MOTOR_DESIGN_LABELS,
  isEnergyEfficientDesign,
} from '../tables/necTable430_52';
import { roundToStandardFuse } from '../tables/necTable240_6A';

/**
 * Motor circuit protector (instantaneous-trip-only breaker) frame sizes in
 * amperes. The frame is the continuous-current rating and must be at least the
 * motor full-load current; the magnetic trip is then set per NEC 430.52(C)(3).
 * Sizes follow the common HMCP / Mag-Gard frame series.
 */
export const MCP_FRAME_SIZES = [3, 7, 15, 30, 50, 100, 150, 250, 400, 600, 800, 1200];

/**
 * Smallest MCP frame whose continuous rating is at least the motor FLA, or
 * null when the FLA exceeds the largest frame carried.
 */
export function getMCPFrameSize(fla: number): number | null {
  for (const frame of MCP_FRAME_SIZES) {
    if (frame >= fla) return frame;
  }
  return null;
}

/**
 * Default instantaneous trip margin above LRA
 * MCP trip setting should be above LRA to avoid nuisance tripping during starting.
 * Typical practice: Set MCP at 110-120% of LRA, then round to next standard size.
 */
export const MCP_LRA_MARGIN = 1.15;

export interface InstantaneousTripLimits {
  /** Table 430.52(C)(1) row the limits came from. */
  designLabel: string;
  /** Table 430.52(C)(1) maximum instantaneous-trip setting, % of FLA (800; 1100 for Design B energy-efficient / premium efficiency). */
  maxPercent: number;
  maxAmps: number;
  /** 430.52(C)(3) Exception No. 1 ceiling where the motor will not otherwise start (1300; 1700 for Design B EE). */
  exceptionPercent: number;
  exceptionAmps: number;
}

/**
 * Maximum instantaneous-trip setting for an MCP per NEC 430.52(C)(3) and
 * Table 430.52, based on the Table 430.250/430.248 full-load current
 * (NEC 430.6(A)(1)), not the nameplate.
 */
export function getInstantaneousTripLimits(fla: number, design: MotorDesign): InstantaneousTripLimits {
  const maxPercent = getMCPMaxPercent(design, 'instantaneous-trip') ?? 800;
  const exceptionPercent = maxPercent >= 1100 ? 1700 : 1300;
  return {
    designLabel: isEnergyEfficientDesign(design) ? MOTOR_DESIGN_LABELS['B-EFF'] : OTHER_SQUIRREL_CAGE_LABEL,
    maxPercent,
    maxAmps: (fla * maxPercent) / 100,
    exceptionPercent,
    exceptionAmps: (fla * exceptionPercent) / 100,
  };
}

export interface InstantaneousTripSetting {
  /** The setting, amperes: FLA × the Table 430.52(C)(1) maximum for the motor type. */
  amps: number;
  /** The setting as a percentage of the table FLC (1100 or 800). */
  percentOfFla: number;
  /** The multiple of FLA the setting is (11 or 8). */
  multiple: number;
  /** Locked-rotor amps the setting is checked against. */
  lra: number;
  /** Margin the check wants above LRA (1.15 = 115%). */
  lraMargin: number;
  /** How far above LRA the setting sits (amps ÷ LRA). */
  marginOverLra: number;
  /**
   * True when the table maximum is below LRA × margin — the motor may not
   * start on it, and 430.52(C)(3) Exception No. 1 (1300% / 1700%) applies.
   */
  needsException: boolean;
  limits: InstantaneousTripLimits;
}

/**
 * Where to set the MCP's instantaneous-trip dial: at the Table 430.52(C)(1)
 * maximum for the motor type — 11 × FLA for a Design B energy-efficient /
 * premium efficiency motor, 8 × FLA for every other squirrel-cage motor.
 * The magnetic element has to ride through the locked-rotor inrush and its
 * asymmetrical first cycle every start, and a premium-efficiency motor's
 * inrush runs to 8 × FLA, so the setting goes to the maximum the code
 * allows rather than to a margin above an estimated LRA. LRA is kept as a
 * check: where the maximum is under LRA × 115% the motor may not start on
 * it, and 430.52(C)(3) Exception No. 1 permits 1300% / 1700%.
 */
export function getInstantaneousTripSetting(
  fla: number,
  lra: number,
  design: MotorDesign,
  lraMargin: number = MCP_LRA_MARGIN
): InstantaneousTripSetting {
  const limits = getInstantaneousTripLimits(fla, design);
  const amps = limits.maxAmps;
  return {
    amps,
    percentOfFla: limits.maxPercent,
    multiple: limits.maxPercent / 100,
    lra,
    lraMargin,
    marginOverLra: lra > 0 ? amps / lra : 0,
    needsException: amps < lra * lraMargin,
    limits,
  };
}

/** The Table 430.52(C)(1) instantaneous-trip row every non-energy-efficient squirrel-cage motor falls in. */
export const OTHER_SQUIRREL_CAGE_LABEL = 'All other squirrel-cage motors (Design A, B standard efficiency, C, D)';

/**
 * The two Table 430.52(C)(1) instantaneous-trip rows a squirrel-cage motor
 * can fall in, energy-efficient / premium first (the row that applies to
 * every general-purpose motor sold since EISA).
 */
export const INSTANTANEOUS_TRIP_MOTOR_TYPES: { design: MotorDesign; label: string }[] = [
  { design: 'B-EFF', label: MOTOR_DESIGN_LABELS['B-EFF'] },
  { design: 'B', label: OTHER_SQUIRREL_CAGE_LABEL },
];

/**
 * The trip setting worked for each Table 430.52(C)(1) motor-type row, so an
 * explanation can show what changes with the motor type. `current` marks the
 * row the given design falls in.
 */
export function getInstantaneousTripSettingsByType(
  fla: number,
  lra: number,
  design: MotorDesign,
  lraMargin: number = MCP_LRA_MARGIN
): { design: MotorDesign; label: string; current: boolean; setting: InstantaneousTripSetting }[] {
  const currentDesign: MotorDesign = isEnergyEfficientDesign(design) ? 'B-EFF' : 'B';
  return INSTANTANEOUS_TRIP_MOTOR_TYPES.map((row) => ({
    ...row,
    current: row.design === currentDesign,
    setting: getInstantaneousTripSetting(fla, lra, row.design, lraMargin),
  }));
}

/**
 * NEC 250.122(D)(2): where the branch-circuit device is an instantaneous-trip
 * breaker or motor short-circuit protector, the equipment grounding conductor
 * is sized from Table 250.122 using the maximum permitted dual-element
 * time-delay fuse: 175% of FLA (Table 430.52), rounded up to the next standard
 * size per 430.52(C)(1) Exception No. 1.
 * @returns the fuse rating (amperes) to look up in Table 250.122
 */
export function getInstantaneousTripEGCBasis(fla: number): number {
  return roundToStandardFuse(fla * 1.75);
}

export const NEC_250_122_D2_REF: NECReference = {
  table: 'NEC 250.122(D)(2)',
  article: '250.122(D)(2)',
  description: 'EGC for instantaneous-trip breaker or MCP circuits sized from the maximum dual-element time-delay fuse rating',
};

/**
 * Input parameters for MCP sizing calculation
 */
export interface MCPSizingInput {
  /** Motor full-load amps from NEC table (not nameplate) */
  fla: number;
  /** Locked rotor amps (for instantaneous trip sizing) */
  lra?: number;
  /** Motor design letter per NEMA MG-1 */
  motorDesign: MotorDesign;
  /** Type of protection device */
  protectionDevice: ProtectionDeviceType;
  /** Whether a VFD/soft starter is present */
  vfdEnabled?: boolean;
}

/**
 * Result of MCP sizing calculation
 */
export interface MCPSizingResult {
  /** Recommended MCP/breaker size in amps */
  mcpAmps: number;
  /** Standard size per NEC 240.6(A) */
  standardSize: number;
  /** Maximum percentage allowed per NEC 430.52 */
  maxPercent: number;
  /** Maximum amps allowed per NEC 430.52 */
  maxAmps: number;
  /** Whether the calculated size is within limits */
  withinLimits: boolean;
  /** Sizing method description */
  method: string;
  /** Additional notes or warnings */
  notes: string[];
  /** NEC references used */
  references: NECReference[];
}

/**
 * VFD input breaker sizing multiplier
 * Per NEC 430.130(A)(1), conductors supplying ASD shall have ampacity
 * not less than 125% of rated input current.
 * Typical practice: Size input breaker at 150% of motor FLA for protection margin.
 */
const VFD_INPUT_BREAKER_MULTIPLIER = 1.5;

/**
 * Calculate the appropriate MCP or breaker size for a motor circuit
 *
 * @param input - Motor parameters and protection device type
 * @returns MCP sizing result with standard size and NEC references
 */
export function calculateMCPSize(input: MCPSizingInput): MCPSizingResult {
  const { fla, lra, motorDesign, protectionDevice, vfdEnabled } = input;

  const notes: string[] = [];
  const references: NECReference[] = [NEC_TABLE_430_52_REF, NEC_SECTION_240_6_REF];

  // Get maximum percentage allowed for this motor design and device type
  const maxPercent = getMCPMaxPercent(motorDesign, protectionDevice);
  if (maxPercent === undefined) {
    return {
      mcpAmps: 0,
      standardSize: 0,
      maxPercent: 0,
      maxAmps: 0,
      withinLimits: false,
      method: 'Error: Invalid motor design or protection device type',
      notes: ['Unable to determine maximum percentage for given parameters'],
      references,
    };
  }

  // Calculate maximum allowed amps per NEC 430.52
  const maxAmps = (fla * maxPercent) / 100;

  let mcpAmps: number;
  let method: string;

  if (vfdEnabled) {
    // VFD/Soft Start: Size input breaker for VFD protection, not motor starting
    // Per NEC 430.130, use thermal-magnetic breaker at ~150% FLA
    mcpAmps = fla * VFD_INPUT_BREAKER_MULTIPLIER;
    method = `VFD input protection: ${(VFD_INPUT_BREAKER_MULTIPLIER * 100).toFixed(0)}% × FLA`;
    notes.push('VFD input breaker protects drive and input wiring, not motor');
    notes.push('Motor protection provided by VFD internal functions');

    // Add VFD-specific reference
    references.push({
      table: 'NEC 2023 Section 430.130',
      article: '430.130',
      description: 'Adjustable Speed Drive Systems - Branch Circuit Conductors',
    });
  } else if (protectionDevice === 'instantaneous-trip') {
    if (lra && lra > 0) {
      // MCP (instantaneous trip only): Size based on LRA
      // Must be set above LRA to allow motor starting without nuisance tripping
      mcpAmps = lra * MCP_LRA_MARGIN;
      method = `Instantaneous trip: ${(MCP_LRA_MARGIN * 100).toFixed(0)}% × LRA (${lra.toFixed(0)} A)`;
      notes.push('MCP trip setting must exceed LRA for reliable starting');

      // Check if calculated MCP exceeds table maximum
      if (mcpAmps > maxAmps) {
        notes.push(`Warning: Calculated MCP (${mcpAmps.toFixed(0)} A) exceeds table maximum (${maxAmps.toFixed(0)} A)`);
        notes.push('Consider reduced voltage starting or larger wire size');
      }
    } else {
      // No locked-rotor data: set the trip at the Table 430.52(C)(1) maximum
      // for the motor type (the setting every premium-efficiency motor needs
      // to ride through its inrush), never at a fuse percentage.
      mcpAmps = maxAmps;
      method = `Instantaneous trip: ${maxPercent}% × FLA per NEC Table 430.52(C)(1) (no locked-rotor data; set at the table maximum)`;
      notes.push('Locked-rotor current not provided; verify the setting rides through the motor inrush (430.52(C)(3))');
    }
  } else if (protectionDevice === 'inverse-time') {
    // Thermal-magnetic breaker: Size based on FLA with table maximum
    // Standard practice: Use table maximum (typically 250%)
    mcpAmps = maxAmps;
    method = `Inverse-time breaker: ${maxPercent}% × FLA per NEC Table 430.52`;
    notes.push('Thermal-magnetic breaker provides both short-circuit and overload protection');
  } else {
    // Fuses: Size based on FLA with table maximum
    mcpAmps = maxAmps;
    method = `${protectionDevice === 'dual-element-fuse' ? 'Dual-element' : 'Non-time-delay'} fuse: ${maxPercent}% × FLA per NEC Table 430.52`;
  }

  // Round to next standard size per NEC 240.6(A)
  const standardSize = roundToStandardSize(mcpAmps);

  // Check if standard size is within limits
  // Per NEC 430.52(C)(1), next higher standard size is permitted if calculated value
  // does not correspond to a standard size
  const withinLimits = standardSize <= roundToStandardSize(maxAmps);

  if (!withinLimits && !vfdEnabled) {
    notes.push(`Standard size ${standardSize} A exceeds maximum allowed ${roundToStandardSize(maxAmps)} A`);
    notes.push('Per NEC 430.52(C)(1) Exception, may need larger protection or reduced voltage starting');
  }

  // Add design-specific notes
  if (isEnergyEfficientDesign(motorDesign)) {
    notes.push('Design B energy-efficient / premium efficiency motor: higher inrush; Table 430.52(C)(1) permits an 1100% instantaneous trip');
  } else if (motorDesign === 'D') {
    notes.push('Design D motor: High slip design for high-inertia loads');
  }

  return {
    mcpAmps: Math.round(mcpAmps * 10) / 10,
    standardSize,
    maxPercent,
    maxAmps: Math.round(maxAmps * 10) / 10,
    withinLimits,
    method,
    notes,
    references,
  };
}

/**
 * Get recommended protection device type based on application
 *
 * @param vfdEnabled - Whether VFD is present
 * @param motorHP - Motor horsepower
 * @returns Recommended device type and explanation
 */
export function getRecommendedProtectionDevice(
  vfdEnabled: boolean,
  motorHP: number
): { device: ProtectionDeviceType; reason: string } {
  if (vfdEnabled) {
    return {
      device: 'inverse-time',
      reason: 'Thermal-magnetic breaker recommended for VFD input protection',
    };
  }

  if (motorHP <= 2) {
    return {
      device: 'dual-element-fuse',
      reason: 'Dual-element fuse provides good protection for small motors',
    };
  }

  if (motorHP <= 50) {
    return {
      device: 'inverse-time',
      reason: 'Thermal-magnetic breaker common for medium motors, provides thermal protection backup',
    };
  }

  return {
    device: 'instantaneous-trip',
    reason: 'MCP (instantaneous trip only) typical for large motors; requires separate overload relay',
  };
}

/**
 * Calculate inverse-time breaker setting for motor circuit
 * This provides an alternative calculation when MCP is not available
 *
 * @param fla - Motor full-load amps
 * @param design - Motor design letter
 * @returns Breaker setting and explanation
 */
export function calculateInverseTimeBreaker(
  fla: number,
  design: MotorDesign
): { size: number; method: string } {
  const maxPercent = getMCPMaxPercent(design, 'inverse-time') || 250;
  const calculatedAmps = (fla * maxPercent) / 100;
  const standardSize = roundToStandardSize(calculatedAmps);

  return {
    size: standardSize,
    method: `${maxPercent}% × ${fla} A FLA = ${calculatedAmps.toFixed(0)} A → ${standardSize} A standard`,
  };
}

/**
 * NEC reference for MCP sizing calculations
 */
export const NEC_SECTION_430_52_REF: NECReference = {
  table: 'NEC 2023 Section 430.52',
  article: '430.52',
  description: 'Rating or Setting for Individual Motor Circuit - Short-Circuit and Ground-Fault Protection',
};
